import { useCallback, useEffect, useState } from "react";
import {
  FormAlert,
  FormCard,
  FormField,
  FormGrid,
  FormSection,
  SettingsButton,
} from "@NP-Webber/crm-kit";
import {
  nedarimApi,
  type NedarimConnectionStatus,
  type NedarimSyncRun,
} from "../../api/nedarimApi";
import { toClientApiError } from "../../api/httpClient";

function webhookLabel(row: { url: string | null; matched: boolean } | undefined) {
  if (!row) return "לא נטען";
  if (row.matched) return "מחובר";
  if (row.url) return "כתובת אחרת";
  return "לא מוגדר";
}

function webhookTone(row: { url: string | null; matched: boolean } | undefined) {
  if (!row) return "muted";
  if (row.matched) return "ok";
  if (row.url) return "warn";
  return "muted";
}

/** Mirrors server assertExternalWebhookUrl — https origin, no local host, no explicit port. */
function isPublicWebhookUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") return false;
    const host = parsed.hostname.toLowerCase();
    if (
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "::1" ||
      host.endsWith(".local")
    ) {
      return false;
    }
    if (parsed.port && parsed.port !== "443") return false;
    return true;
  } catch {
    return false;
  }
}

function connectionAlert(status: NedarimConnectionStatus | null): {
  severity: "info" | "success" | "warning";
  text: string;
} {
  if (!status?.configured) {
    return { severity: "info", text: "טרם הוגדר" };
  }
  const keyPart = status.hasApiPassword
    ? ` · סיסמה: ${status.apiPasswordMasked || "****"}`
    : "";
  if (status.hasApiPassword) {
    return { severity: "success", text: `מחובר${keyPart}` };
  }
  return { severity: "warning", text: `מוסד מוגדר ללא סיסמת API${keyPart}` };
}

export function NedarimConnectionPanel() {
  const [status, setStatus] = useState<NedarimConnectionStatus | null>(null);
  const [runs, setRuns] = useState<NedarimSyncRun[]>([]);
  const [mosadNumber, setMosadNumber] = useState("");
  const [apiPassword, setApiPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [item, syncRuns] = await Promise.all([
        nedarimApi.getConnection(),
        nedarimApi.listSyncRuns().catch(() => [] as NedarimSyncRun[]),
      ]);
      setStatus(item);
      setMosadNumber(item.mosadNumber ?? "");
      setRuns(syncRuns);
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function runAction(fn: () => Promise<unknown>, okMessage: string) {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await fn();
      setSuccess(okMessage);
      await load();
      setApiPassword("");
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setBusy(false);
    }
  }

  const alert = connectionAlert(status);
  const hasApi = Boolean(status?.hasApiPassword);

  if (loading) {
    return (
      <section
        className="settings-kit-panel"
        aria-label="נדרים פלוס — חיבור וסנכרון"
      >
        <FormAlert severity="info">טוען חיבור…</FormAlert>
      </section>
    );
  }

  return (
    <section
      className="settings-kit-panel"
      aria-label="נדרים פלוס — חיבור וסנכרון"
    >
      <FormCard
        title="נדרים פלוס — חיבור וסנכרון"
        info="חיבור חד-פעמי ברמת הארגון. התנועות נשמרות ב«תנועות מנדרים פלוס» ואינן נרשמות אוטומטית ליתרת מנויים."
        error={error}
        success={success}
      >
        <FormSection title="סטטוס">
          <FormAlert severity={alert.severity}>{alert.text}</FormAlert>
        </FormSection>

        <FormSection title="פרטי מוסד">
          <FormGrid cols={2}>
            <FormField
              label="קוד מוסד"
              value={mosadNumber}
              onChange={setMosadNumber}
              disabled={busy}
            />
            <FormField
              label={
                status?.hasApiPassword
                  ? `סיסמת API (${status.apiPasswordMasked || "****"})`
                  : "סיסמת API (npk_…)"
              }
              type="password"
              value={apiPassword}
              onChange={setApiPassword}
              placeholder={
                status?.hasApiPassword ? "השאירו ריק כדי לא לשנות" : ""
              }
              disabled={busy}
            />
          </FormGrid>
          <div className="nedarim-connection__actions">
            <SettingsButton
              type="button"
              variant="primarySettings"
              disabled={busy || !mosadNumber.trim()}
              onClick={() =>
                void runAction(
                  () =>
                    nedarimApi.saveConnection({
                      mosadNumber: mosadNumber.trim(),
                      apiPassword: apiPassword.trim() || undefined,
                    }),
                  "החיבור נשמר",
                )
              }
            >
              שמירת חיבור
            </SettingsButton>
            {status?.createKeyUrl ? (
              <SettingsButton
                type="button"
                variant="settings"
                disabled={busy}
                onClick={() => {
                  window.open(
                    status.createKeyUrl!,
                    "_blank",
                    "noopener,noreferrer",
                  );
                }}
              >
                צור מפתח API בנדרים
              </SettingsButton>
            ) : null}
          </div>
        </FormSection>

        <FormSection title="Webhooks">
          {status?.webhookUrl ? (
            <p className="nedarim-connection__mono">{status.webhookUrl}</p>
          ) : (
            <FormAlert severity="info">
              שמרו חיבור כדי לקבל כתובת Webhook.
            </FormAlert>
          )}
          {status?.webhookUrl && !isPublicWebhookUrl(status.webhookUrl) ? (
            <FormAlert severity="warning">
              כתובת ה-Webhook אינה ציבורית (HTTP / localhost / פורט מקומי). בפרודקשן
              יש להגדיר API_PUBLIC_URL לכתובת ה-HTTPS של ה-API (לא של הממשק) ואז
              לרשום מחדש. לא ניתן לרשום כתובת זו בנדרים.
            </FormAlert>
          ) : null}
          {status?.webhookError ? (
            <FormAlert severity="warning">{status.webhookError}</FormAlert>
          ) : null}
          <ul className="nedarim-connection__webhook-list">
            {(
              [
                ["עסקאות", status?.webhooks?.transaction],
                ["הקמת הו״ק", status?.webhooks?.keva],
                ["סירובים", status?.webhooks?.siruv],
              ] as const
            ).map(([label, row]) => (
              <li key={label} className="nedarim-connection__webhook-row">
                <span>{label}</span>
                <span
                  className={`subscription-grid__pill subscription-grid__pill--${webhookTone(row)}`}
                >
                  {webhookLabel(row)}
                </span>
              </li>
            ))}
          </ul>
          <div className="nedarim-connection__actions">
            <SettingsButton
              type="button"
              variant="primarySettings"
              disabled={
                busy ||
                !hasApi ||
                !isPublicWebhookUrl(status?.webhookUrl)
              }
              onClick={() =>
                void runAction(
                  () => nedarimApi.registerWebhooks(true),
                  "Webhooks נרשמו בנדרים (×3)",
                )
              }
            >
              רשום Webhooks בנדרים (×3)
            </SettingsButton>
            <SettingsButton
              type="button"
              variant="settings"
              disabled={busy || !hasApi || Boolean(status?.hmacEnabled)}
              onClick={() =>
                void runAction(() => nedarimApi.enableHmac(), "HMAC הופעל")
              }
            >
              {status?.hmacEnabled ? "HMAC פעיל" : "הפעל חתימת HMAC"}
            </SettingsButton>
          </div>
        </FormSection>

        <FormSection title="סנכרון API">
          <p className="nedarim-connection__meta">
            סנכרון אחרון:{" "}
            {status?.lastSyncAt
              ? new Date(status.lastSyncAt).toLocaleString("he-IL")
              : "עדיין לא רץ"}
          </p>
          <div className="nedarim-connection__actions">
            <SettingsButton
              type="button"
              variant="primarySettings"
              disabled={busy || !hasApi}
              onClick={() =>
                void runAction(() => nedarimApi.runSync(), "הסנכרון הושלם")
              }
            >
              הרץ סנכרון עכשיו
            </SettingsButton>
          </div>
          {runs.length ? (
            <ul className="nedarim-connection__runs">
              {runs.slice(0, 5).map((run) => (
                <li key={run.id}>
                  {new Date(run.startedAt).toLocaleString("he-IL")} — {run.status}
                  {run.status === "success"
                    ? ` (עסקאות ${run.transactionsUpserted}, הו״ק ${run.mandatesUpserted}, חיובים ${run.chargesUpserted})`
                    : run.errorMessage
                      ? ` — ${run.errorMessage}`
                      : ""}
                </li>
              ))}
            </ul>
          ) : null}
        </FormSection>
      </FormCard>
    </section>
  );
}
